<!-- eslint-disable vue/require-toggle-inside-transition -->
<template>
  <div>
    <!-- desktop -->
    <Transition name="slide-fade-down">
      <div class="hidden md:block w-full h-16 items-center" :class="routeNavBackground">
        <div class="relative px-16 2xl:px-40 flex w-full h-full items-center justify-between">
          <div class="flex justify-between items-center">
            <div class="flex items-center gap-8">
              <RightClickable :close-on-click="true">
                <NuxtLink :to="{ name: 'index' }" class="h-full w-32 cursor-pointer">
                  <img src="/assets/icons/ceto-cceto.svg" class="w-24 transition-opacity duration-500 hover:opacity-50" />
                </NuxtLink>
                <template #menu>
                  <div class="p-2 px-4 text-wave-crest-50 font-light text-nowrap bg-inky-depths-300 border border-wave-crest-600 rounded">
                    <p>
                      Looking for our brand assets?
                      <NuxtLink :to="{ name: 'design' }" class="underline">Click here!</NuxtLink>
                    </p>
                  </div>
                </template>
              </RightClickable>
              <div v-for="(item, index) in menuItems" :key="index" class="grow">
                <NuxtLink
                  v-if="!item.subItems"
                  :to="{ name: item?.name }"
                  class="text-wave-crest-400 hover:text-wave-crest-50 hover:cursor-pointer"
                  :class="route.name == item.name ? 'text-wave-crest-50 border-b border-slate-200' : 'text-wave-crest-400'">
                  {{ item.display }}
                </NuxtLink>
                <div v-else class="relative">
                  <button
                    class="flex items-center gap-2 transition-colors hover:text-wave-crest-50 text-wave-crest-400"
                    @click="toggleDropdown(item.name)">
                    <p>{{ item.display }}</p>
                    <div class="p-1 aspect-square">
                      <Icon name="chevron-down" class="w-3 h-3 fill-wave-crest-50" />
                    </div>
                  </button>

                  <Transition name="fade-in">
                    <MarketingProductDropdown v-if="dropdownVisible === item.name" class="absolute top-10" :product-items="item.subItems" />
                  </Transition>
                </div>
              </div>
            </div>
          </div>
          <div class="flex items-center gap-4 text-sm font-light text-nowrap">
            <NuxtLink :to="{ name: 'login' }" class="px-4 py-2 transition-opacity hover:opacity-70">Sign In</NuxtLink>
            <NuxtLink :to="{ name: 'contact' }" class="px-6 py-2 bg-ocean-surge-700 rounded-full transition-opacity hover:opacity-70">
              Book A Demo
            </NuxtLink>
          </div>
        </div>
      </div>
    </Transition>

    <!-- mobile -->
    <Transition name="slide-fade-down">
      <div
        class="flex md:hidden absolute w-full h-16 items-center justify-between px-8 py-8 transition-colors duration-300"
        :class="[showHeader ? 'bg-glass' : '', { 'bg-ocean-surge-700': showMobileMenu }]">
        <NuxtLink :to="{ name: 'index' }"> <img src="/assets/icons/ceto-cceto.svg" class="w-24 -ml-1" /> </NuxtLink>
        <ButtonMenu class="lg:hidden w-5 h-4" bg-color="bg-slate-100" :open="showMobileMenu" @click="toggleShowMobileMenu" />
        <Transition name="slide-left">
          <MarketingNavigationMobileMenu
            v-if="showMobileMenu"
            class="fixed flex sm:mt-20 -z-10"
            @close-mobile-menu="setShowMobileMenu(false)" />
        </Transition>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { menuItems } from '~/config'
const route = useRoute()
const { showMobileMenu, setShowMobileMenu, toggleShowMobileMenu } = useMobileMenu()
const { showProductDropdown } = useShowProductDropdown()

const showHeader = ref<boolean>(false)
const routeNavBackground = ref<string>('bg-transparent')
const scrolledDistance = ref<number>(0)
const dropdownVisible = ref<string>('')

const handleScroll = () => {
  const currentScroll = window.scrollY
  const viewportHeight = window.innerHeight
  const windowHasScrolledPastThreshold = currentScroll / viewportHeight > 0.05

  showHeader.value = windowHasScrolledPastThreshold
  routeNavBackground.value = windowHasScrolledPastThreshold ? 'bg-glass' : 'bg-transparent'
}

// Adjust route-specific nav background
const handleRouteOptions = (routeName: string) => {
  routeNavBackground.value = 'bg-transparent'
  switch (routeName) {
    case 'index':
      scrolledDistance.value = 0
      break
    case 'design':
      routeNavBackground.value = 'bg-glass'
      break
    case 'news-slug':
      routeNavBackground.value = 'bg-glass'
      break
    case 'forgot-password':
      routeNavBackground.value = 'bg-inky-depths-950'
      break
    case 'reset-password':
      routeNavBackground.value = 'bg-inky-depths-950'
      break
  }
}

const toggleDropdown = (name: string) => {
  dropdownVisible.value = dropdownVisible.value === name ? '' : name
}

onMounted(() => {
  window.addEventListener('scroll', handleScroll)
  handleRouteOptions(route.name?.toString() || '')
  showHeader.value = false // Show on desktop, hide on mobile at load
})

watch(
  () => route.name,
  () => {
    handleRouteOptions(route.name?.toString() || '')
    showProductDropdown.value = false
    setShowMobileMenu(false)
    showHeader.value = false
    dropdownVisible.value = ''
  }
)

watch(showMobileMenu, (newValue) => {
  newValue ? (document.body.style.overflow = 'hidden') : (document.body.style.overflow = 'auto')
})
</script>
